"use client"
import { useEffect, useMemo, useState } from 'react'
import { useUserStore } from '@/app/stores/useUserStore';
import { Bell, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'
import InviteDialog from '@/app/components/Me/Invite/invite-dialog'
import { BIND_INVITE_MODAL_CACHE_KEY, BIND_INVITE_MODAL_CACHE_DURATION } from '@/app/const/locale'
import { api, type UserProfile } from '@/lib/api'
import { message } from 'antd'
import { useTranslation } from 'react-i18next'

export default function CoInvite({
    className,
    size = 'md',
    closeable = true,
}: {
    className?: string
    size?: 'sm' | 'md'
    closeable?: boolean
}) {
    const { t } = useTranslation()
    const { userInfo, setUserInfo } = useUserStore()
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [dismissedUntil, setDismissedUntil] = useState<number | null>(null)
    const hasInviteCode = !!userInfo?.inviter
    /** -1 未申请；0 待审核；1 通过；2 拒绝 */
    const applicationStatus = userInfo?.bindStatus?.applicationStatus ?? -1
    const applicationLocked = applicationStatus === 0 || applicationStatus === 2
    const isApplicationRejected = applicationStatus === 2
    const joinDate = useMemo(() => dayjs(userInfo?.joinDate), [userInfo?.joinDate])
    const [now, setNow] = useState(() => dayjs())
    const [isApplying, setIsApplying] = useState(false)

    useEffect(() => {
        if (hasInviteCode) return
        const timer = window.setInterval(() => setNow(dayjs()), 1000)
        return () => window.clearInterval(timer)
    }, [hasInviteCode])

    useEffect(() => {
        if (applicationLocked) setIsDialogOpen(false)
    }, [applicationLocked])

    const dismissKey = useMemo(() => {
        const uid = (userInfo as any)?.userId ?? (userInfo as any)?.id ?? 'anon'
        return BIND_INVITE_MODAL_CACHE_KEY + uid
    }, [userInfo])

    const handleExpiredConfirm = async (code: string, reason: string) => {
        if (applicationLocked || isApplying) return
        setIsApplying(true)
        const inviterCode = code.replace(/\s/g, '').trim()
        if (!inviterCode) {
            message.warning(t('invite.enterInviteCode'))
            setIsApplying(false)
            return
        }
        try {
            const res = await api.user.applyManualBindInvite({
                inviterCode,
                reason: reason?.trim() || undefined,
            })
            if (res.code !== 200) {
                message.error(res.msg || t('invite.bindBanner.applySubmitFailed'))
                return
            }
            message.success(t('invite.bindBanner.applySubmitted'))
            setIsDialogOpen(false)
            setUserInfo({
                ...userInfo as UserProfile,
                bindStatus: {
                    inviteCode: code,
                    bindReason: reason,
                    applicationStatus: 0,
                    applicationRejectReason: '',
                },
            })
        } catch {
            message.error(t('invite.bindBanner.applySubmitFailedRetry'))
        } finally {
            setIsApplying(false)
        }
    }
    useEffect(() => {
        try {
            const closedTime = localStorage.getItem(BIND_INVITE_MODAL_CACHE_KEY + userInfo?.userId)
            if (!closedTime) return
            const timeDiff = Date.now() - parseInt(closedTime, 10);
            if (timeDiff >= BIND_INVITE_MODAL_CACHE_DURATION) {
                setDismissedUntil(null)
            } else {
                setDismissedUntil(Date.now() + BIND_INVITE_MODAL_CACHE_DURATION - timeDiff)
            }
        } catch {
            // ignore
        }
    }, [userInfo?.userId])

    const { countdownText, isExpired } = useMemo(() => {
        const status = userInfo?.bindStatus?.applicationStatus ?? -1

        if (status === 1) {
            return { countdownText: null, isExpired: false }
        }
        if (status === 0) {
            return {
                countdownText: t('invite.bindBanner.pendingReview'),
                isExpired: false,
            }
        }
        if (status === 2) {
            const reason = userInfo?.bindStatus?.applicationRejectReason?.trim()
            return {
                countdownText: reason
                    ? t('invite.bindBanner.rejectedWithReason', { reason })
                    : t('invite.bindBanner.rejectedGeneric'),
                isExpired: false,
            }
        }

        if (!joinDate.isValid()) return { countdownText: null, isExpired: false }

        const deadline = joinDate.add(72, 'hour')
        const diffMs = deadline.valueOf() - now.valueOf()

        if (diffMs <= 0) {
            return {
                countdownText: t('invite.bindBanner.expiredHint'),
                isExpired: true,
            }
        }

        const totalSeconds = Math.ceil(diffMs / 1000)
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        const seconds = totalSeconds % 60
        return {
            countdownText: t('invite.bindBanner.countdown', { hours, minutes, seconds }),
            isExpired: false,
        }
    }, [joinDate, now, userInfo?.bindStatus, t])

    if (hasInviteCode) return null
    if (!countdownText) return null
    if (dismissedUntil != null && now.valueOf() < dismissedUntil) return null

    const sizeStyles = size === 'sm'
        ? {
            wrap: "gap-2 px-2 py-1 rounded-md",
            icon: "size-3",
            text: "text-[12px]",
            btn: "h-6 px-2.5 text-xs rounded-md",
            closeWrap: "p-0.5",
            closeIcon: "w-3.5 h-3.5",
        }
        : {
            wrap: "gap-2 p-1 pl-3 rounded-lg",
            icon: "size-3",
            text: "text-[13px]",
            btn: "h-7 px-3 text-xs rounded-lg",
            closeWrap: "p-1",
            closeIcon: "w-4 h-4",
        }
    if (!userInfo?.userId) {
        return null
    }
    return (
        <>
            <div
                className={cn(
                    "flex items-center border",
                    isApplicationRejected
                        ? "bg-[#f5e4e4] dark:bg-background-card border-red-500/55 text-red-500"
                        : "bg-[#e1efde] dark:bg-background-card border-primary/60 text-primary",
                    sizeStyles.wrap,
                    className
                )}
            >
                <Bell className={sizeStyles.icon} fill='currentColor' />

                <span
                    className={cn(
                        "flex-1 min-w-0",
                        isApplicationRejected ? "text-red-500" : "text-primary",
                        sizeStyles.text
                    )}
                >
                    {countdownText}
                </span>

                {!applicationLocked && (
                    <Button
                        className={cn(sizeStyles.btn)}
                        onClick={() => setIsDialogOpen(true)}
                    >
                        {isExpired ? t('invite.bindBanner.goApply') : t('invite.bindBanner.goBind')}
                    </Button>
                )}
                {closeable && (
                    <div
                        className={cn(sizeStyles.closeWrap, 'rounded-sm cursor-pointer hover:bg-background-3/20 transition-all duration-300 text-font-primary')}
                        onClick={() => {
                            const until = dayjs().add(24, 'hour').valueOf()
                            setDismissedUntil(until)
                            setIsDialogOpen(false)
                            try {
                                window.localStorage.setItem(dismissKey, String(until))
                            } catch {
                                // ignore
                            }
                        }}
                    >
                        <X className={sizeStyles.closeIcon} />
                    </div>
                )}
            </div>
            <InviteDialog
                title={(
                    <div
                        className={cn(
                            'rounded-lg group min-w-0 py-1 text-center border',
                            isApplicationRejected
                                ? "bg-[#f5e4e4] dark:bg-background-card border-red-500/55 text-red-500"
                                : "bg-[#e1efde] dark:bg-background-card border-primary/60 text-primary",
                            sizeStyles.text
                        )}
                    >
                        {countdownText}
                    </div>
                )}
                isLoading={isApplying}
                isExpired={isExpired}
                allowSkip={false}
                applicationSubmitLocked={applicationLocked}
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
                onExpiredConfirm={(code, errorMsg) => {
                    handleExpiredConfirm(code, errorMsg)
                }}
            />
        </>
    )
}